import { type ApiClient, ApiError } from '@classgo/app-kit';
import type {
  AttendanceAlert,
  AttendanceDay,
  AttendanceExcuse,
  AttendanceMarkResult,
  AttendanceMarksInput,
  AttendanceSettings,
  AttendanceSettingsInput,
  ExcuseReviewResult,
  ReviewExcuseInput,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

import type { ExcuseAttachmentLink, ExcuseFilter } from './types';

const BASE = '/school/attendance';

const qs = (query: Record<string, string | undefined>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) params.set(key, value);
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

/**
 * The attendance's write side (#114 over the API of #105): the marks of a session or a day saved
 * in one call (idempotent per student and slot, so saving twice changes nothing), the review of
 * an excuse, the link to its file and the school's threshold.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const attendanceApi = (api: ApiClient) => ({
  mark: (body: AttendanceMarksInput) =>
    api.request<AttendanceMarkResult>(BASE, { method: 'PUT', body }),
  review: (id: string, body: ReviewExcuseInput) =>
    api.request<ExcuseReviewResult>(`${BASE}/excuses/${id}/review`, { method: 'POST', body }),
  attachment: (id: string) => api.request<ExcuseAttachmentLink>(`${BASE}/excuses/${id}/attachment`),
  saveSettings: (body: AttendanceSettingsInput) =>
    api.request<AttendanceSettings>(`${BASE}/settings`, { method: 'PUT', body }),
});

/**
 * The attendance reads: a group's roll on a day or in a session, the review queue (pending
 * first), the threshold and the alerts. Every key starts with `['school', 'attendance']`, so one
 * invalidation after a write refreshes them all.
 */
export const attendanceQueries = {
  roll: (api: ApiClient, groupId: string, date: string, blockId: string | null) =>
    queryOptions({
      queryKey: ['school', 'attendance', 'roll', groupId, date, blockId],
      queryFn: () =>
        api.request<AttendanceDay>(
          `${BASE}${qs({ groupId, date, ...(blockId ? { blockId } : {}) })}`,
        ),
      enabled: groupId !== '' && date !== '',
    }),
  excuses: (api: ApiClient, filter: ExcuseFilter) =>
    queryOptions({
      queryKey: ['school', 'attendance', 'excuses', filter],
      queryFn: () =>
        api.request<{ items: AttendanceExcuse[] }>(
          `${BASE}/excuses${qs(filter === 'all' ? {} : { status: filter })}`,
        ),
    }),
  settings: (api: ApiClient) =>
    queryOptions({
      queryKey: ['school', 'attendance', 'settings'],
      queryFn: () => api.request<AttendanceSettings>(`${BASE}/settings`),
    }),
  alerts: (api: ApiClient) =>
    queryOptions({
      queryKey: ['school', 'attendance', 'alerts'],
      queryFn: () => api.request<{ items: AttendanceAlert[] }>(`${BASE}/alerts`),
    }),
};

/**
 * The API's own refusal when it wrote one for the person (a date outside the school year, a
 * session of another group, a decision already taken): the message of a 409 or of a 400 that is
 * not the schema's ("Invalid …"). Null otherwise, so the screen falls back to `describeError`.
 *
 * @param {unknown} thrown -- Whatever the call threw.
 * @returns The refusal to show, or null.
 */
export const refusalOf = (thrown: unknown): string | null =>
  thrown instanceof ApiError &&
  (thrown.code === 'CONFLICT' ||
    (thrown.code === 'VALIDATION_FAILED' && !thrown.message.startsWith('Invalid ')))
    ? thrown.message
    : null;
